import React, { FormEvent, ForwardedRef, forwardRef, KeyboardEvent, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { guid } from '@/src/infrastructure/guid/guid';
import { CSeverity, NativeProps } from '@/src/components/interfaces';
import { AlignItems, CFlex, FlexDirection, JustifyContent } from '@/src/components/c-flex/CFlex';
import { CFieldValidation, FieldValidation } from '@/src/components/c-field-validations/CFieldValidation';
import { CLoader, CLoaderSize } from '@/src/components/c-loader/CLoader';
import { CText, CTextColor } from '@/src/components/c-text/CText';

export interface CCheckboxProps extends NativeProps {
  label?: string;
  checked?: boolean;
  disabled?: boolean;
  indeterminate?: boolean;
  validations?: FieldValidation[];
  loading?: boolean;
  onChange?: (event: {
    target: any;
    type?: any;
  }) => (Promise<void | boolean> | void | boolean);
  onBlur?: (event: {
    target: any;
    type?: any;
  }) => Promise<void | boolean>;
  onFocus?: (event: {
    target: any;
    type?: any;
  }) => Promise<void | boolean>;
  onKeyUp?: (event: KeyboardEvent) => (Promise<void> | void);
  onKeyDown?: (event: KeyboardEvent) => (Promise<void> | void);
  ref?: (instance: any) => void;
  name?: string;
  min?: string | number;
  max?: string | number;
  maxLength?: number;
  minLength?: number;
  pattern?: string;
  required?: boolean;
  hideMessageContainer?: boolean;
}

export const CCheckbox = forwardRef(function CCheckbox(props: PropsWithChildren<CCheckboxProps>, ref: ForwardedRef<HTMLInputElement>): React.JSX.Element {
  const [id] = useState(guid('checkbox'));
  const [checked, setChecked] = useState<boolean>(props.checked ?? false);
  const checkbox = useRef<HTMLInputElement | null>(null);
  const validations = props.validations ?? [];

  const status = useMemo(() => {
    const hasErrors = validations.some(error => error.severity === CSeverity.Error);
    const hasWarnings = validations.some(error => error.severity === CSeverity.Warning);
    const hasInfo = validations.some(error => error.severity === CSeverity.Info);
    const hasSuccess = validations.some(error => error.severity === CSeverity.Success);

    if (hasErrors) return 'error';
    if (hasWarnings) return 'warning';
    if (hasInfo) return 'info';
    if (hasSuccess) return 'success';

    return 'neutral';
  }, [validations]);

  const onClick = useCallback((event: FormEvent) => {
    if (props.disabled) return;

    setChecked(prevState => !prevState);

    props.onChange?.(event);
  }, [props]);

  useEffect(() => {
    setChecked(props.checked ?? false);
  }, [props.checked]);

  useEffect(() => {
    if (checkbox.current) {
      checkbox.current!.indeterminate = props.indeterminate ?? false;
    }
  }, [props.indeterminate]);

  return (
    <CFlex direction={FlexDirection.Column} class="c-gap--sm">
      <label id={props.id} htmlFor={id} className={classNames('c-checkbox', 'tap--disabled', {
        [`c-checkbox--status-${status}`]: status,
        'c-checkbox--disabled': props.disabled,
      })}>
        <input
          ref={(el: HTMLInputElement | null) => {
            (ref as ((instance: HTMLInputElement | null) => void))?.(el);
            checkbox.current = el;
          }}
          name={props.name}
          pattern={props.pattern}
          required={props.required}
          className={classNames('c-checkbox__input', props.class)}
          data-test-selector={props.testSelector}
          onChange={onClick}
          onBlur={props.onBlur}
          onFocus={props.onFocus}
          onKeyUp={props.onKeyUp}
          onKeyDown={props.onKeyDown}
          disabled={props.disabled}
          aria-disabled={props.disabled}
          type="checkbox"
          checked={checked}
          id={id}
        />
        {props.loading && (
          <CFlex justifyContent={JustifyContent.Center} alignItems={AlignItems.Center} class="c-checkbox__loader">
            <CLoader size={CLoaderSize.Xs}/>
          </CFlex>
        )}
        {!props.loading && (
          <>
            <div className="c-checkbox__shadow" />
            <svg className="c-checkbox__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 19 19">
              <rect
                width="18"
                height="18"
                x=".5"
                y=".5"
                rx="3"
                strokeWidth="1"
                className={classNames( {
                  'indeterminate': props.indeterminate,
                  'checked': checked,
                })}
              />
              <path
                className={classNames({
                  'indeterminate': props.indeterminate,
                  'checked': checked,
                })}
                strokeLinecap="round"
                strokeWidth="2"
                d={!props.indeterminate ? 'M4 10l4 4 7-7' : 'M 4 10 l 11 0'}
              />
            </svg>
          </>
        )}
        {props.label && (
          <CText class="c-checkbox__label" color={CTextColor.White}>
            {props.label}
          </CText>
        )}
      </label>

      {validations.length > 0 && !props.hideMessageContainer && (
        <CFieldValidation validations={validations} testSelector="checkbox-validation"/>
      )}
    </CFlex>
  );
});